// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { AutocompleteItemModel, ProductAutocompleteItemModel } from "@insite/client-framework/Types/ApiModels";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { xClassNames } from "@west/XDL/Utilities";
import React from "react";
import { css } from "styled-components";

// XNG-Changes: updated goToUrl params
interface Props {
    products: ProductAutocompleteItemModel[];
    focusedItem?: AutocompleteItemModel;
    goToUrl: (url: string, track?: boolean, autoCompleteResultLength?: number, clickLabel?: string) => void;
    extendedStyles?: AutocompleteProductsStyles;
    // XNG-Change-WEST-154
    query?: string;
    doSearch?: () => void;
}

export interface AutocompleteProductsStyles {
    headerText?: TypographyPresentationProps;
    container?: GridContainerProps;
    focusedContainer?: GridContainerProps;
    imageGridItem?: GridItemProps;
    image?: LazyImageProps;
    infoGridItem?: GridItemProps;
    titleLink?: LinkPresentationProps;
    erpNumberText?: TypographyPresentationProps;
    // XNG-Change:
    productContainer?: InjectableCss;
    headerContainer?: InjectableCss;
    viewAllLink?: LinkPresentationProps;
    descriptionContainer?: InjectableCss;
    productInfoContainer?: InjectableCss;
    brandLink?: LinkPresentationProps;
}

export const autocompleteProductsStyles: AutocompleteProductsStyles = {
    headerText: {
        weight: "bold",
        css: css`
            text-align: left;
            margin: 10px 0 5px 0;
        `,
    },
    container: {
        gap: 0,
        css: css`
            flex-grow: 0;
            margin: 5px 0;
            cursor: pointer;
        `,
    },
    focusedContainer: {
        gap: 0,
        css: css`
            flex-grow: 0;
            margin: 5px 0;
            cursor: pointer;
            background-color: lightgray;
        `,
    },
    imageGridItem: {
        width: 3,
        css: css`
            max-width: 65px;
            max-height: 50px;
            flex-basis: 65px;
        `,
    },
    image: {
        width: "50px",
        css: css`
            img {
                height: 100%;
            }
        `,
    },
    infoGridItem: {
        width: 9,
        css: css`
            flex-direction: column;
            max-width: calc(100% - 65px);
            flex-basis: calc(100% - 65px);
        `,
    },
    titleLink: {
        typographyProps: {
            ellipsis: true,
            css: css`
                width: 100%;
                text-align: left;
                margin: 0;
            `,
        },
        css: css`
            width: 270px;
        `,
    },
    erpNumberText: {
        ellipsis: true,
        css: css`
            width: 270px;
            text-align: left;
            margin: 5px 0 0 0;
        `,
    },
};

const styles = autocompleteProductsStyles;

class AutocompleteProducts extends React.Component<Props> {
    private readonly styles: AutocompleteProductsStyles;

    constructor(props: Props) {
        super(props);

        this.styles = mergeToNew(styles, props.extendedStyles);
    }

    render() {
        const { products } = this.props;
        const styles = this.styles;

        // XNG-Changes
        return (
            <StyledWrapper {...styles.container}>
                <StyledWrapper {...styles.headerContainer}>
                    <Typography
                        {...styles.headerText}
                        data-test-selector="autocompleteProducts_Header"
                        className="autocomplete-products-header"
                    >
                        {translate("Suggested or Related Products")} ({products.length})
                    </Typography>
                    <Link {...styles.viewAllLink} onClick={this.props.doSearch} aria-label={translate("View All")}>
                        {translate("View All")}
                    </Link>
                </StyledWrapper>
                {/* XNG-Changes: updated goToUrl call */}
                {products.map(product => {
                    const hasSeparate = product.manufacturerItemNumber;
                    return (
                        <StyledWrapper
                            {...styles.productContainer}
                            key={product.id!}
                            className="autocomplete-products-container"
                        >
                            <StyledWrapper
                                {...styles.imageGridItem}
                                className="autocomplete-products-image-grid-item"
                                onClick={() => {
                                    this.props.goToUrl(product.url, undefined, undefined, product.title);
                                }}
                            >
                                <LazyImage
                                    {...styles.image}
                                    src={product.image}
                                    className="autocomplete-products-image"
                                />
                            </StyledWrapper>
                            <StyledWrapper {...styles.infoGridItem} className="autocomplete-products-info-grid-item">
                                <StyledWrapper {...styles.descriptionContainer}>
                                    <Link
                                        {...styles.titleLink}
                                        data-test-selector={`productId_${product.id}`}
                                        className="autocomplete-products-link"
                                        onClick={() => {
                                            this.props.goToUrl(product.url, undefined, undefined, product.title);
                                        }}
                                    >
                                        {product.displayTitle}
                                    </Link>
                                </StyledWrapper>
                                <StyledWrapper {...styles.productInfoContainer}>
                                    {product.brandName && product.brandDetailPagePath && (
                                        // XNG-Changes sent brandName
                                        <Link
                                            {...styles.brandLink}
                                            onClick={() => {
                                                this.props.goToUrl(
                                                    product.brandDetailPagePath,
                                                    undefined,
                                                    undefined,
                                                    product.brandName,
                                                );
                                            }}
                                            className={xClassNames.append(hasSeparate && "add-separate")}
                                            aria-label={product.brandName}
                                        >
                                            {product.brandName}
                                        </Link>
                                    )}
                                    {product.manufacturerItemNumber && (
                                        <Typography
                                            {...styles.erpNumberText}
                                            data-test-selector="autocompleteProducts_PartNumber"
                                            className="autocomplete-products-erp-number"
                                        >
                                            {/* {product.isNameCustomerOverride ? ( */}
                                            {/*    <> */}
                                            {/*        {translate("My Part #")} */}
                                            {/*        {product.displayName} */}
                                            {/*    </> */}
                                            {/* ) : ( */}
                                            {/*    <> */}
                                            {/*        {translate("Part #")} */}
                                            {/*        {product.displayErpNumber} */}
                                            {/*    </> */}
                                            {/* )} */}
                                            <>
                                                {translate("SKU")}: {product.displayManufacturerItemNumber}
                                            </>
                                        </Typography>
                                    )}
                                </StyledWrapper>
                            </StyledWrapper>
                        </StyledWrapper>
                    );
                })}
            </StyledWrapper>
        );
    }
}

export default AutocompleteProducts;
